import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { 
  Tag, Check, Crown, Star, Gem, Diamond, Sparkles,
  Image, Music, Video, FileText, Link as LinkIcon, 
  Clock, Zap, Minus, Plus, ShoppingCart, Wallet, Gift, Ticket
} from 'lucide-react';
import api from '../../utils/api';
import { useAuth } from '../../context/AuthContext';
import { toast } from 'sonner';

const TIER_STYLES = {
  SILVER: { bg: 'from-gray-400 to-gray-600', icon: Tag, color: 'text-gray-300' },
  GOLD: { bg: 'from-yellow-400 to-yellow-600', icon: Star, color: 'text-yellow-400' },
  DIAMOND: { bg: 'from-cyan-400 to-blue-600', icon: Diamond, color: 'text-cyan-400' },
  VIP: { bg: 'from-purple-400 to-purple-600', icon: Crown, color: 'text-purple-400' },
  PREMIUM: { bg: 'from-pink-400 to-rose-600', icon: Sparkles, color: 'text-pink-400' },
};

const BuyPass = () => {
  const { user, refreshUser } = useAuth();
  const navigate = useNavigate();
  const [tiers, setTiers] = useState([]);
  const [myPasses, setMyPasses] = useState([]);
  const [loading, setLoading] = useState(true);
  const [quantities, setQuantities] = useState({});
  const [paymentMethods, setPaymentMethods] = useState({});
  const [purchasing, setPurchasing] = useState(null);
  const [walletInfo, setWalletInfo] = useState({ cash: 0, commission: 0 });
  const [couponCode, setCouponCode] = useState('');
  const [redeemingCoupon, setRedeemingCoupon] = useState(false);

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const [tiersRes, passesRes, userRes] = await Promise.all([
        api.get('/p2p/tiers'),
        api.get('/p2p/my-passes').catch(() => ({ data: [] })),
        api.get('/auth/me').catch(() => ({ data: {} }))
      ]);
      
      setTiers(tiersRes.data);
      setMyPasses(passesRes.data);
      
      // Initialize quantities and payment methods
      const initialQty = {};
      const initialPayment = {};
      tiersRes.data.forEach(t => {
        initialQty[t.id] = 1;
        initialPayment[t.id] = 'cash'; // Default to 100% cash
      });
      setQuantities(initialQty);
      setPaymentMethods(initialPayment);
      
      // Get wallet info
      if (userRes.data) {
        setWalletInfo({
          cash: userRes.data.cash_wallet || 0,
          commission: userRes.data.commission_wallet || 0
        });
      }
    } catch (error) {
      console.error('Failed to fetch data:', error);
    } finally {
      setLoading(false);
    }
  };

  const getPassCount = (tierName) => {
    const pass = myPasses.find(p => p.tier_name === tierName);
    return pass?.quantity || 0;
  };

  const handleBuyPass = async (tier) => {
    if (!user) {
      toast.error('Please login to purchase passes');
      navigate('/login');
      return;
    }

    const qty = quantities[tier.id] || 1;
    const total = tier.price * qty;
    const paymentMethod = paymentMethods[tier.id] || 'cash';

    // Check wallet balance BEFORE making API call
    if (paymentMethod === 'cash') {
      if (walletInfo.cash < total) {
        toast.error(`Insufficient Cash Wallet balance! Need ₹${total.toLocaleString()} but you have ₹${walletInfo.cash.toLocaleString()}`);
        return;
      }
    } else if (paymentMethod === 'hybrid') {
      const halfCost = total / 2;
      if (walletInfo.cash < halfCost) {
        toast.error(`Insufficient Cash Wallet for 50/50 split! Need at least ₹${halfCost.toLocaleString()} Cash (you have ₹${walletInfo.cash.toLocaleString()})`);
        return;
      }
      if (walletInfo.cash + walletInfo.commission < total) {
        toast.error(`Insufficient total balance! Need ₹${total.toLocaleString()} combined (Cash: ₹${walletInfo.cash.toLocaleString()}, Commission: ₹${walletInfo.commission.toLocaleString()})`);
        return;
      }
    }

    setPurchasing(tier.id);
    try {
      const response = await api.post('/p2p/buy-pass', {
        tier_id: tier.id,
        quantity: qty,
        payment_method: paymentMethod
      });
      
      if (response.data.success) {
        toast.success(`🎉 Successfully purchased ${qty}x ${tier.name} pass!`, {
          description: `₹${total.toLocaleString()} deducted from your wallet`,
          duration: 5000
        });
        fetchData();
        if (refreshUser) refreshUser();
      } else {
        toast.error(response.data.message || 'Purchase failed');
      }
    } catch (error) {
      const errorMsg = error.response?.data?.detail || 'Purchase failed. Please try again.';
      toast.error(errorMsg, { duration: 5000 });
    } finally {
      setPurchasing(null);
    }
  };

  const handleRedeemCoupon = async () => {
    if (!couponCode.trim()) {
      toast.error('Please enter a coupon code');
      return;
    }
    
    setRedeemingCoupon(true);
    try {
      const response = await api.post(`/p2p/redeem-coupon?coupon_code=${couponCode.trim()}`);
      toast.success(response.data.message);
      setCouponCode('');
      fetchData();
      if (refreshUser) refreshUser();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Invalid coupon code');
    } finally {
      setRedeemingCoupon(false);
    }
  };

  const updateQuantity = (tierId, delta) => {
    setQuantities(prev => ({
      ...prev,
      [tierId]: Math.max(1, (prev[tierId] || 1) + delta)
    }));
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="animate-spin w-10 h-10 border-4 border-primary border-t-transparent rounded-full"></div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background py-8 px-4">
      <div className="max-w-6xl mx-auto">
        {/* Header */}
        <div className="text-center mb-10">
          <div className="inline-flex items-center gap-2 bg-primary/10 px-4 py-2 rounded-full text-primary text-sm font-medium mb-4">
            <Tag className="w-4 h-4" />
            Listing Passes
          </div>
          <h1 className="text-3xl md:text-4xl font-bold mb-4">
            Buy Listing Passes
          </h1>
          <p className="text-muted-foreground max-w-2xl mx-auto">
            Each pass allows you to create one listing (Buy Request or Sell Offer). 
            Higher tiers unlock more features and higher value limits.
          </p>
        </div>

        {/* Wallet Info */}
        <div className="glass-card p-4 mb-8 flex flex-wrap items-center justify-center gap-6">
          <div className="flex items-center gap-3">
            <Wallet className="w-5 h-5 text-green-500" />
            <div>
              <p className="text-xs text-muted-foreground">Cash Wallet</p>
              <p className="font-bold text-green-500">₹{walletInfo.cash.toLocaleString()}</p>
            </div>
          </div>
          <div className="w-px h-10 bg-white/10"></div>
          <div className="flex items-center gap-3">
            <Wallet className="w-5 h-5 text-blue-500" />
            <div>
              <p className="text-xs text-muted-foreground">Commission Wallet</p>
              <p className="font-bold text-blue-500">₹{walletInfo.commission.toLocaleString()}</p>
            </div>
          </div>
          <div className="w-px h-10 bg-white/10"></div>
          <div className="text-xs text-muted-foreground max-w-xs text-center">
            <strong className="text-green-500">100% Cash</strong> always available. Choose your payment method below.
          </div>
        </div>

        {/* Coupon Redemption */}
        <div className="glass-card p-6 mb-8">
          <div className="flex items-center gap-3 mb-4">
            <Gift className="w-6 h-6 text-yellow-500" />
            <h3 className="font-semibold text-lg">Have a Coupon Code?</h3>
          </div>
          <div className="flex gap-3">
            <div className="flex-1 relative">
              <Ticket className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-muted-foreground" />
              <input
                type="text"
                value={couponCode}
                onChange={(e) => setCouponCode(e.target.value.toUpperCase())}
                placeholder="Enter coupon code (e.g., SIL123ABC)"
                className="w-full pl-12 pr-4 py-3 bg-white/5 border border-white/10 rounded-xl focus:border-yellow-500 uppercase"
              />
            </div>
            <button
              onClick={handleRedeemCoupon}
              disabled={redeemingCoupon || !couponCode.trim()}
              className="px-6 py-3 bg-gradient-to-r from-yellow-500 to-orange-500 text-black font-semibold rounded-xl hover:opacity-90 transition-all disabled:opacity-50 flex items-center gap-2"
            >
              {redeemingCoupon ? (
                <div className="animate-spin w-5 h-5 border-2 border-black border-t-transparent rounded-full" />
              ) : (
                <>
                  <Gift className="w-5 h-5" />
                  Redeem
                </>
              )}
            </button>
          </div>
          <p className="text-xs text-muted-foreground mt-2">
            Enter your coupon code to get free listing passes!
          </p>
        </div>

        {/* Pass Tiers */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {tiers.map((tier) => {
            const style = TIER_STYLES[tier.name] || TIER_STYLES.SILVER;
            const Icon = style.icon;
            const qty = quantities[tier.id] || 1;
            const total = tier.price * qty;
            const owned = getPassCount(tier.name);

            return (
              <div 
                key={tier.id}
                className={`glass-card overflow-hidden relative ${tier.name === 'PREMIUM' ? 'ring-2 ring-pink-500' : ''}`}
              >
                {/* Header Gradient */}
                <div className={`bg-gradient-to-r ${style.bg} p-6`}>
                  <div className="flex items-center justify-between">
                    <div>
                      <Icon className="w-10 h-10 text-white mb-2" />
                      <h3 className="text-2xl font-bold text-white">{tier.name}</h3>
                    </div>
                    <div className="text-right">
                      <p className="text-white/70 text-sm">Price</p>
                      <p className="text-3xl font-bold text-white">₹{tier.price}</p>
                    </div>
                  </div>
                </div>

                {/* Content */}
                <div className="p-6">
                  {/* Value Limit */}
                  <div className="text-center mb-4 py-3 bg-white/5 rounded-xl">
                    <p className="text-sm text-muted-foreground">List items up to</p>
                    <p className={`text-2xl font-bold ${style.color}`}>
                      ₹{tier.value_limit?.toLocaleString()}
                    </p>
                  </div>

                  {/* Features */}
                  <div className="space-y-3 mb-6">
                    <div className="flex items-center gap-3">
                      <Image className={`w-5 h-5 ${tier.max_images > 0 ? 'text-green-500' : 'text-muted-foreground'}`} />
                      <span className="text-sm">
                        {tier.max_images > 0 ? `Up to ${tier.max_images} Images` : 'No Images'}
                      </span>
                    </div>
                    
                    <div className="flex items-center gap-3">
                      <Music className={`w-5 h-5 ${tier.allow_audio ? 'text-green-500' : 'text-muted-foreground'}`} />
                      <span className={`text-sm ${!tier.allow_audio && 'text-muted-foreground line-through'}`}>
                        Audio Upload
                      </span>
                      {tier.allow_audio && <Check className="w-4 h-4 text-green-500" />}
                    </div>
                    
                    <div className="flex items-center gap-3">
                      <Video className={`w-5 h-5 ${tier.allow_video ? 'text-green-500' : 'text-muted-foreground'}`} />
                      <span className={`text-sm ${!tier.allow_video && 'text-muted-foreground line-through'}`}>
                        Video Upload
                      </span>
                      {tier.allow_video && <Check className="w-4 h-4 text-green-500" />}
                    </div>
                    
                    <div className="flex items-center gap-3">
                      <FileText className={`w-5 h-5 ${tier.allow_pdf ? 'text-green-500' : 'text-muted-foreground'}`} />
                      <span className={`text-sm ${!tier.allow_pdf && 'text-muted-foreground line-through'}`}>
                        PDF Attachment
                      </span>
                      {tier.allow_pdf && <Check className="w-4 h-4 text-green-500" />}
                    </div>
                    
                    <div className="flex items-center gap-3">
                      <Clock className={`w-5 h-5 ${tier.allow_timer ? 'text-green-500' : 'text-muted-foreground'}`} />
                      <span className={`text-sm ${!tier.allow_timer && 'text-muted-foreground line-through'}`}>
                        Timer / Expiry
                      </span>
                      {tier.allow_timer && <Check className="w-4 h-4 text-green-500" />}
                    </div>
                    
                    <div className="flex items-center gap-3">
                      <Zap className={`w-5 h-5 ${tier.allow_custom_buttons ? 'text-green-500' : 'text-muted-foreground'}`} />
                      <span className={`text-sm ${!tier.allow_custom_buttons && 'text-muted-foreground line-through'}`}>
                        Custom Buttons
                      </span>
                      {tier.allow_custom_buttons && <Check className="w-4 h-4 text-green-500" />}
                    </div>
                    
                    {tier.priority_listing && (
                      <div className="flex items-center gap-3">
                        <Star className="w-5 h-5 text-yellow-500" />
                        <span className="text-sm font-semibold text-yellow-500">
                          Priority Listing
                        </span>
                        <Check className="w-4 h-4 text-green-500" />
                      </div>
                    )}
                  </div>

                  {/* Owned Passes */}
                  {owned > 0 && (
                    <div className="bg-green-500/10 border border-green-500/30 rounded-xl p-3 mb-4 text-center">
                      <p className="text-green-500 font-semibold">
                        You own: {owned} pass{owned > 1 ? 'es' : ''}
                      </p>
                    </div>
                  )}

                  {/* Quantity Selector */}
                  <div className="flex items-center justify-between bg-white/5 rounded-xl p-3 mb-4">
                    <span className="text-sm text-muted-foreground">Quantity:</span>
                    <div className="flex items-center gap-3">
                      <button
                        onClick={() => updateQuantity(tier.id, -1)}
                        className="w-8 h-8 flex items-center justify-center bg-white/10 rounded-lg hover:bg-white/20"
                      >
                        <Minus className="w-4 h-4" />
                      </button>
                      <span className="w-8 text-center font-bold">{qty}</span>
                      <button
                        onClick={() => updateQuantity(tier.id, 1)}
                        className="w-8 h-8 flex items-center justify-center bg-white/10 rounded-lg hover:bg-white/20"
                      >
                        <Plus className="w-4 h-4" />
                      </button>
                    </div>
                  </div>

                  {/* Payment Method Selector */}
                  <div className="mb-4">
                    <p className="text-xs text-muted-foreground mb-2">Pay with:</p>
                    <div className="grid grid-cols-2 gap-2">
                      <button
                        type="button"
                        onClick={() => setPaymentMethods(prev => ({ ...prev, [tier.id]: 'cash' }))}
                        className={`p-2 rounded-lg text-xs font-medium transition-all ${
                          paymentMethods[tier.id] === 'cash'
                            ? 'bg-green-500/20 border-2 border-green-500 text-green-500'
                            : 'bg-white/5 border border-white/10 hover:border-white/30'
                        }`}
                      >
                        100% Cash
                      </button>
                      <button
                        type="button"
                        onClick={() => setPaymentMethods(prev => ({ ...prev, [tier.id]: 'hybrid' }))}
                        className={`p-2 rounded-lg text-xs font-medium transition-all ${
                          paymentMethods[tier.id] === 'hybrid'
                            ? 'bg-purple-500/20 border-2 border-purple-500 text-purple-500'
                            : 'bg-white/5 border border-white/10 hover:border-white/30'
                        }`}
                      >
                        50% Cash + 50% Comm
                      </button>
                    </div>
                  </div>

                  {/* Total & Buy Button */}
                  <div className="flex items-center justify-between mb-4">
                    <span className="text-muted-foreground">Total:</span>
                    <span className="text-2xl font-bold">₹{total.toLocaleString()}</span>
                  </div>

                  {/* Insufficient Balance Warning */}
                  {(() => {
                    const method = paymentMethods[tier.id] || 'cash';
                    const insufficientCash = method === 'cash' && walletInfo.cash < total;
                    const insufficientHybrid = method === 'hybrid' && (walletInfo.cash < total/2 || walletInfo.cash + walletInfo.commission < total);
                    
                    if (insufficientCash || insufficientHybrid) {
                      return (
                        <div className="bg-red-500/10 border border-red-500/30 rounded-xl p-3 mb-4 text-center" data-testid="low-balance-warning">
                          <p className="text-red-500 text-sm font-semibold">
                            ⚠️ Insufficient Balance
                          </p>
                          <p className="text-red-400 text-xs mt-1">
                            {method === 'cash' 
                              ? `Need ₹${total.toLocaleString()} Cash (You have ₹${walletInfo.cash.toLocaleString()})`
                              : `Need ₹${(total/2).toLocaleString()} Cash + ₹${(total/2).toLocaleString()} Commission`
                            }
                          </p>
                        </div>
                      );
                    }
                    return null;
                  })()}

                  <button
                    onClick={() => handleBuyPass(tier)}
                    disabled={purchasing === tier.id || (() => {
                      const method = paymentMethods[tier.id] || 'cash';
                      if (method === 'cash') return walletInfo.cash < total;
                      return walletInfo.cash < total/2 || walletInfo.cash + walletInfo.commission < total;
                    })()}
                    className={`w-full py-3 rounded-xl font-semibold flex items-center justify-center gap-2 transition-all ${
                      purchasing === tier.id 
                        ? 'bg-white/10 cursor-wait' 
                        : (() => {
                            const method = paymentMethods[tier.id] || 'cash';
                            const insufficient = method === 'cash' 
                              ? walletInfo.cash < total 
                              : (walletInfo.cash < total/2 || walletInfo.cash + walletInfo.commission < total);
                            return insufficient 
                              ? 'bg-white/10 cursor-not-allowed opacity-50' 
                              : `bg-gradient-to-r ${style.bg} hover:opacity-90`;
                          })()
                    }`}
                  >
                    {purchasing === tier.id ? (
                      <>
                        <div className="animate-spin w-5 h-5 border-2 border-white border-t-transparent rounded-full"></div>
                        Processing...
                      </>
                    ) : (
                      <>
                        <ShoppingCart className="w-5 h-5" />
                        Buy {qty} Pass{qty > 1 ? 'es' : ''}
                      </>
                    )}
                  </button>
                </div>
              </div>
            );
          })}
        </div>

        {/* Info Section */}
        <div className="mt-12 glass-card p-6">
          <h3 className="text-xl font-bold mb-4">How Listing Passes Work</h3>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            <div className="flex gap-4">
              <div className="w-10 h-10 bg-primary/20 rounded-xl flex items-center justify-center flex-shrink-0">
                <Tag className="w-5 h-5 text-primary" />
              </div>
              <div>
                <h4 className="font-semibold mb-1">1. Buy a Pass</h4>
                <p className="text-sm text-muted-foreground">
                  Choose a tier based on your listing value and features needed.
                </p>
              </div>
            </div>
            <div className="flex gap-4">
              <div className="w-10 h-10 bg-primary/20 rounded-xl flex items-center justify-center flex-shrink-0">
                <Plus className="w-5 h-5 text-primary" />
              </div>
              <div>
                <h4 className="font-semibold mb-1">2. Create Listing</h4>
                <p className="text-sm text-muted-foreground">
                  Use your pass to post a "Sell Offer" or "Buy Request".
                </p>
              </div>
            </div>
            <div className="flex gap-4">
              <div className="w-10 h-10 bg-primary/20 rounded-xl flex items-center justify-center flex-shrink-0">
                <ShoppingCart className="w-5 h-5 text-primary" />
              </div>
              <div>
                <h4 className="font-semibold mb-1">3. Connect & Deal</h4>
                <p className="text-sm text-muted-foreground">
                  Get responses from interested buyers/sellers and close deals.
                </p>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default BuyPass;
